import { notificationsStore } from '@/core/store/notifications.store';
import { notificationsService } from '@/core/services/notifications.service';
import type { NotificationBase } from '@/core/types';
import { toJsDateOrNull } from '@shared/fechas/fecha-like';
import modalHtml from './modal.html?raw';

/**
 * @file src/components/modal/modal.ts
 * @description Componente modal genérico reutilizable para diferentes tipos de modales
 */

type ModalType = 'notifications' | 'general';

let isInitialized = false;
let currentModalType: ModalType | null = null;
let currentFilter = 'all';
let lastNotification: NotificationBase | null = null;
let isLoadingMore = false;

/**
 * Inicializa el sistema de modal genérico
 */
export function initModal(): void {
  if (isInitialized) return;

  try {
    // Inyectar HTML del modal genérico en el body
    const container = document.createElement('div');
    container.id = 'modal-container';
    container.innerHTML = modalHtml;
    document.body.appendChild(container);

    // Configurar event listeners globales
    setupGlobalEventListeners();

    isInitialized = true;
    console.log('📋 Sistema de modal genérico inicializado');

  } catch (error) {
    console.error('Error inicializando sistema de modal:', error);
  }
}

/**
 * Abre el modal de notificaciones
 */
export function openNotificationsModal(): void {
  if (!isInitialized) {
    console.warn('Modal no inicializado. Inicializando...');
    initModal();
  }

  currentModalType = 'notifications';
  
  // Inyectar contenido específico de notificaciones
  const modalContainer = document.getElementById('modalContainer');
  const template = document.getElementById('notificationsModalTemplate') as HTMLTemplateElement;
  
  if (!modalContainer || !template) {
    console.error('Elementos del modal no encontrados');
    return;
  }

  // Limpiar contenido anterior
  modalContainer.innerHTML = '';
  
  // Clonar template de notificaciones
  const clone = template.content.cloneNode(true);
  modalContainer.appendChild(clone);

  // Mostrar modal
  const modal = document.getElementById('genericModal');
  if (modal) {
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  // Configurar eventos específicos de notificaciones
  setupNotificationsEventListeners();
  
  // Suscribirse a cambios del store
  setupNotificationsStoreSubscription();

  // Cargar notificaciones iniciales
  loadInitialNotifications();

  // Actualizar estado de permisos
  updatePermissionStatus();
}

/**
 * Cierra el modal actual
 */
export function closeModal(): void {
  const modal = document.getElementById('genericModal');
  if (!modal) return;

  modal.classList.add('hidden');
  document.body.style.overflow = '';
  
  // Reset estado
  currentModalType = null;
  currentFilter = 'all';
  lastNotification = null;
  setActiveFilter('all');
}

/**
 * Configura event listeners globales
 */
function setupGlobalEventListeners(): void {
  // Escuchar evento personalizado para abrir modal de notificaciones
  document.addEventListener('open-notifications-modal', () => {
    openNotificationsModal();
  });

  // Cerrar modal con tecla ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  });
}

/**
 * Configura event listeners específicos para el modal de notificaciones
 */
function setupNotificationsEventListeners(): void {
  const modal = document.getElementById('genericModal');
  const closeBtn = document.getElementById('closeModal');
  const markAllBtn = document.getElementById('markAllAsRead');
  const loadMoreBtn = document.getElementById('loadMoreNotifications');
  const enableNotificationsBtn = document.getElementById('enableNotificationsBtn');
  const filterBtns = document.querySelectorAll('.notification-filter-btn');

  // Cerrar modal
  closeBtn?.addEventListener('click', closeModal);
  
  // Cerrar modal al hacer clic en el overlay
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // Marcar todas como leídas
  markAllBtn?.addEventListener('click', markAllNotificationsAsRead);

  // Cargar más notificaciones
  loadMoreBtn?.addEventListener('click', loadMoreNotifications);

  // Habilitar notificaciones
  enableNotificationsBtn?.addEventListener('click', handleEnableNotifications);

  // Filtros de notificaciones
  filterBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const filter = target.closest('[data-filter]')?.getAttribute('data-filter') || 'all';
      setActiveFilter(filter);
    });
  });
}

/**
 * Configura la suscripción al store de notificaciones
 */
function setupNotificationsStoreSubscription(): void {
  notificationsStore.subscribe(state => {
    if (currentModalType === 'notifications') {
      renderNotifications(state.notifications);
      updateLoadingState(state.loading);
    }
  });
}

/**
 * Carga las notificaciones iniciales
 */
async function loadInitialNotifications(): Promise<void> {
  try {
    showLoadingState();
    const state = notificationsStore.getState();
    
    // Si ya hay notificaciones en el store, mostrarlas
    if (state.notifications.length > 0) {
      renderNotifications(state.notifications);
      hideLoadingState();
    } else {
      // Si no hay notificaciones, cargar desde el servicio
      setTimeout(() => {
        const updatedState = notificationsStore.getState();
        renderNotifications(updatedState.notifications);
        hideLoadingState();
      }, 1000);
    }
  } catch (error) {
    console.error('Error cargando notificaciones iniciales:', error);
    hideLoadingState();
    showEmptyState();
  }
}

/**
 * Renderiza las notificaciones en el modal
 */
function renderNotifications(notifications: NotificationBase[]): void {
  const container = document.querySelector('#notificationsList .divide-y');
  if (!container) return;

  // Filtrar notificaciones según el filtro activo
  const filteredNotifications = filterNotifications(notifications);

  // Limpiar contenedor
  container.innerHTML = '';

  if (filteredNotifications.length === 0) {
    showEmptyState();
    hideFooter();
    return;
  }

  hideEmptyState();

  // Renderizar cada notificación
  filteredNotifications.forEach(notification => {
    const notificationElement = createNotificationElement(notification);
    container.appendChild(notificationElement);
  });

  // Mostrar footer si hay notificaciones
  showFooter();

  // Actualizar la última notificación para paginación
  if (filteredNotifications.length > 0) {
    lastNotification = filteredNotifications[filteredNotifications.length - 1];
  }
}

/**
 * Filtra notificaciones según el filtro activo
 * IMPORTANTE: Solo muestra notificaciones que ya deberían mostrarse (fechaProgramada <= ahora)
 * o tareas vencidas (fechaLimite <= ahora)
 */
function filterNotifications(notifications: NotificationBase[]): NotificationBase[] {
  const now = new Date();
  
  // Primero filtrar por fecha: solo mostrar notificaciones que ya deberían mostrarse
  const notificationsToShow = notifications.filter(n => {
    // Convertir fechaProgramada y fechaLimite a Date si es necesario
    const fechaProgramada = toJsDateOrNull(n.fechaProgramada);
    const fechaLimite = toJsDateOrNull(n.fechaLimite);

    // Para tareas vencidas: siempre mostrarlas si fechaLimite <= ahora
    if (n.tipo === 'tarea_vencida' && fechaLimite) {
      return fechaLimite <= now;
    }
    
    // Para otras notificaciones: usar fechaProgramada si existe, sino fechaCreacion
    if (fechaProgramada) {
      return fechaProgramada <= now;
    }
    
    // Si no hay fechaProgramada, usar fechaCreacion (mostrar siempre)
    return true;
  });
  
  // Luego aplicar el filtro seleccionado
  switch (currentFilter) {
    case 'unread':
      return notificationsToShow.filter(n => !n.leida);
    case 'all':
      return notificationsToShow;
    default:
      return notificationsToShow.filter(n => n.tipo === currentFilter);
  }
}

/**
 * Crea un elemento DOM para una notificación
 */
function createNotificationElement(notification: NotificationBase): HTMLElement {
  const template = document.getElementById('notificationItemTemplate') as HTMLTemplateElement;
  if (!template) {
    throw new Error('Template de notificación no encontrado');
  }

  const clone = template.content.cloneNode(true) as DocumentFragment;
  const element = clone.querySelector('.notification-item') as HTMLElement;

  // Configurar atributos
  element.setAttribute('data-notification-id', notification.id);
  
  // Configurar contenido
  const icon = element.querySelector('.notification-icon') as HTMLElement;
  const iconClass = element.querySelector('.notification-icon-class') as HTMLElement;
  const title = element.querySelector('.notification-title') as HTMLElement;
  const message = element.querySelector('.notification-message') as HTMLElement;
  const time = element.querySelector('.notification-time') as HTMLElement;
  const client = element.querySelector('.notification-client') as HTMLElement;
  const unreadIndicator = element.querySelector('.unread-indicator') as HTMLElement;

  // Configurar ícono
  const iconConfig = getNotificationIcon(notification.tipo);
  icon.className = `notification-icon flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconConfig.bg}`;
  iconClass.className = `${iconConfig.icon} text-white`;

  // Configurar contenido
  title.textContent = notification.titulo;
  message.textContent = notification.mensaje;
  
  // Usar fechaProgramada si existe, sino usar fechaCreacion
  const fechaReferencia = notification.fechaProgramada || notification.fechaCreacion;
  time.textContent = formatTimeAgo(fechaReferencia);
  
  if (notification.clienteNombre) {
    client.textContent = `Cliente: ${notification.clienteNombre}`;
  } else {
    client.style.display = 'none';
  }

  // Mostrar indicador de no leída
  if (!notification.leida) {
    unreadIndicator.classList.remove('hidden');
    element.classList.add('bg-blue-50', 'border-l-4', 'border-blue-400');
  }

  // Configurar botones de acción
  setupNotificationActions(element, notification);

  // Aplicar opacidad si la tarea está completada
  if (notification.datos?.tareaCompletada) {
    element.classList.add('opacity-50');
  }

  return element;
}

/**
 * Configura las acciones de una notificación
 */
function setupNotificationActions(element: HTMLElement, notification: NotificationBase): void {
  const completeBtn = element.querySelector('.complete-task-btn') as HTMLElement;
  const viewClientBtn = element.querySelector('.view-client-btn') as HTMLElement;
  const markReadBtn = element.querySelector('.mark-read-btn') as HTMLElement;

  // Botón de completar tarea
  if ((notification.tipo === 'tarea_recordatorio' || notification.tipo === 'tarea_vencida') && 
      !notification.datos?.tareaCompletada) {
    completeBtn.classList.remove('hidden');
    completeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleCompleteTask(notification);
    });
  }

  // Botón de ver cliente
  if (notification.clienteId) {
    viewClientBtn.classList.remove('hidden');
    viewClientBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      navigateToClient(notification.clienteId!);
    });
  }

  // Botón de marcar como leída
  if (!notification.leida) {
    markReadBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      markNotificationAsRead(notification.id);
    });
  } else {
    markReadBtn.style.display = 'none';
  }

  // Click en toda la notificación marca como leída
  element.addEventListener('click', () => {
    if (!notification.leida) {
      markNotificationAsRead(notification.id);
    }
  });
}

/**
 * Maneja el completado de una tarea
 */
async function handleCompleteTask(notification: NotificationBase): Promise<void> {
  try {
    if (!notification.tareaId) return;

    // Llamar al servicio para completar la tarea
    notificationsService.updateTaskCompletion(notification.tareaId, true);
    
    // Actualizar UI inmediatamente
    const element = document.querySelector(`[data-notification-id="${notification.id}"]`);
    if (element) {
      element.classList.add('opacity-50');
      const completeBtn = element.querySelector('.complete-task-btn') as HTMLElement;
      if (completeBtn) {
        completeBtn.style.display = 'none';
      }
    }

    console.log('✅ Tarea completada desde modal:', notification.tareaId);
  } catch (error) {
    console.error('Error completando tarea:', error);
  }
}

/**
 * Navega a la vista del cliente
 */
function navigateToClient(clienteId: string): void {
  closeModal();
  window.location.href = `/vistacliente?id=${clienteId}`;
}

/**
 * Marca una notificación como leída
 */
async function markNotificationAsRead(notificationId: string): Promise<void> {
  try {
    await notificationsService.markAsRead(notificationId);
    
    // Actualizar UI inmediatamente
    const element = document.querySelector(`[data-notification-id="${notificationId}"]`);
    if (element) {
      const unreadIndicator = element.querySelector('.unread-indicator');
      const markReadBtn = element.querySelector('.mark-read-btn') as HTMLElement;
      
      unreadIndicator?.classList.add('hidden');
      element.classList.remove('bg-blue-50', 'border-l-4', 'border-blue-400');
      if (markReadBtn) markReadBtn.style.display = 'none';
    }
  } catch (error) {
    console.error('Error marcando notificación como leída:', error);
  }
}

/**
 * Marca todas las notificaciones como leídas
 */
async function markAllNotificationsAsRead(): Promise<void> {
  try {
    const state = notificationsStore.getState();
    const unreadNotifications = state.notifications.filter(n => !n.leida);
    
    // Marcar todas como leídas
    for (const notification of unreadNotifications) {
      await notificationsService.markAsRead(notification.id);
    }
    
    console.log('✅ Todas las notificaciones marcadas como leídas');
  } catch (error) {
    console.error('Error marcando todas como leídas:', error);
  }
}

/**
 * Carga más notificaciones (paginación)
 */
async function loadMoreNotifications(): Promise<void> {
  if (isLoadingMore || !lastNotification) return;

  try {
    isLoadingMore = true;
    showLoadMoreLoading();

    const moreNotifications = await notificationsService.loadMoreNotifications(lastNotification);
    
    if (moreNotifications.length > 0) {
      // Agregar nuevas notificaciones al store
      const currentState = notificationsStore.getState();
      const allNotifications = [...currentState.notifications, ...moreNotifications];
      notificationsStore.setNotifications(allNotifications);
    } else {
      hideFooter();
    }

  } catch (error) {
    console.error('Error cargando más notificaciones:', error);
  } finally {
    isLoadingMore = false;
    hideLoadMoreLoading();
  }
}

/**
 * Establece el filtro activo
 */
function setActiveFilter(filter: string): void {
  currentFilter = filter;
  
  // Actualizar botones de filtro
  document.querySelectorAll('.notification-filter-btn').forEach(btn => {
    btn.classList.remove('active');
  });
  
  const activeBtn = document.querySelector(`[data-filter="${filter}"]`);
  activeBtn?.classList.add('active');
  
  // Re-renderizar notificaciones
  const state = notificationsStore.getState();
  renderNotifications(state.notifications);
}

/**
 * Obtiene la configuración del ícono para un tipo de notificación
 */
function getNotificationIcon(tipo: string): { icon: string; bg: string } {
  switch (tipo) {
    case 'tarea_recordatorio':
      return { icon: 'fas fa-clock', bg: 'bg-blue-500' };
    case 'tarea_vencida':
      return { icon: 'fas fa-exclamation-triangle', bg: 'bg-red-500' };
    case 'venta_nueva':
      return { icon: 'fas fa-shopping-cart', bg: 'bg-green-500' };
    case 'cliente_nuevo':
      return { icon: 'fas fa-user-plus', bg: 'bg-purple-500' };
    default:
      return { icon: 'fas fa-bell', bg: 'bg-gray-500' };
  }
}

/**
 * Formatea el tiempo transcurrido o tiempo hasta la notificación
 */
function formatTimeAgo(date: unknown): string {
  const now = new Date();
  let notificationDate: Date;

  if (date instanceof Date) {
    notificationDate = date;
  } else if (date && typeof (date as { toDate?: unknown }).toDate === 'function') {
    notificationDate = (date as { toDate: () => Date }).toDate();
  } else if (date && (date as { seconds?: number }).seconds) {
    notificationDate = new Date((date as { seconds: number }).seconds * 1000);
  } else {
    notificationDate = new Date(date as string | number);
  }
  
  const diffMs = notificationDate.getTime() - now.getTime();
  const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
  const diffHours = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));
  const diffDays = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60 * 24));

  // Si la fecha es futura, mostrar "en X tiempo"
  if (diffMs > 0) {
    if (diffMinutes < 60) return `En ${diffMinutes}m`;
    if (diffHours < 24) return `En ${diffHours}h`;
    if (diffDays < 30) return `En ${diffDays}d`;
    return `Programada: ${notificationDate.toLocaleDateString()}`;
  }
  
  // Si la fecha es pasada, mostrar tiempo transcurrido
  if (diffMinutes < 1) return 'Ahora';
  if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 30) return `Hace ${diffDays}d`;
  
  return notificationDate.toLocaleDateString();
}

// Estados UI
function showLoadingState(): void {
  const loading = document.getElementById('notificationsLoading');
  const list = document.getElementById('notificationsList');
  const empty = document.getElementById('notificationsEmpty');
  
  loading?.classList.remove('hidden');
  list?.classList.add('hidden');
  empty?.classList.add('hidden');
}

function hideLoadingState(): void {
  const loading = document.getElementById('notificationsLoading');
  const list = document.getElementById('notificationsList');
  
  loading?.classList.add('hidden');
  list?.classList.remove('hidden');
}

function showEmptyState(): void {
  const empty = document.getElementById('notificationsEmpty');
  const list = document.getElementById('notificationsList');
  
  empty?.classList.remove('hidden');
  empty?.classList.add('flex');
  list?.classList.add('hidden');
}

function hideEmptyState(): void {
  const empty = document.getElementById('notificationsEmpty');
  const list = document.getElementById('notificationsList');
  
  empty?.classList.add('hidden');
  empty?.classList.remove('flex');
  list?.classList.remove('hidden');
}

function showFooter(): void {
  const footer = document.getElementById('notificationsFooter');
  footer?.classList.remove('hidden');
}

function hideFooter(): void {
  const footer = document.getElementById('notificationsFooter');
  footer?.classList.add('hidden');
}

function showLoadMoreLoading(): void {
  const btn = document.getElementById('loadMoreNotifications') as HTMLButtonElement;
  if (btn) {
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Cargando...';
    btn.disabled = true;
  }
}

function hideLoadMoreLoading(): void {
  const btn = document.getElementById('loadMoreNotifications') as HTMLButtonElement;
  if (btn) {
    btn.innerHTML = '<i class="fas fa-arrow-down mr-2"></i>Cargar más notificaciones';
    btn.disabled = false;
  }
}

function updateLoadingState(loading: boolean): void {
  if (loading) {
    showLoadingState();
  } else {
    hideLoadingState();
  }
}

/**
 * Actualiza el estado de permisos de notificación en el modal
 */
function updatePermissionStatus(): void {
  const statusContainer = document.getElementById('notificationPermissionStatus');
  const statusText = document.getElementById('permissionStatusText');
  const enableBtn = document.getElementById('enableNotificationsBtn');
  
  if (!statusContainer || !statusText || !enableBtn) return;

  const { permission, isAvailable } = notificationsService.getNotificationPermissionStatus();

  if (!isAvailable) {
    statusContainer.classList.add('hidden');
    return;
  }

  switch (permission) {
    case 'granted':
      statusContainer.classList.add('hidden');
      break;
      
    case 'denied':
      statusContainer.classList.remove('hidden');
      statusContainer.className = 'bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-sm mr-3';
      statusText.innerHTML = '<i class="fas fa-bell-slash mr-1"></i>Notificaciones bloqueadas';
      enableBtn.textContent = 'Ayuda';
      break;
      
    case 'default':
      statusContainer.classList.remove('hidden');
      statusContainer.className = 'bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm mr-3';
      statusText.innerHTML = '<i class="fas fa-bell mr-1"></i>Notificaciones disponibles';
      enableBtn.textContent = 'Habilitar';
      break;
  }
}

/**
 * Maneja el intento de habilitar notificaciones
 */
async function handleEnableNotifications(): Promise<void> {
  const { permission } = notificationsService.getNotificationPermissionStatus();
  
  if (permission === 'denied') {
    // Mostrar ayuda para permisos denegados
    showPermissionHelp();
    return;
  }

  try {
    const enabled = await notificationsService.requestNotificationPermission();
    if (enabled) {
      updatePermissionStatus();
      console.log('✅ Notificaciones habilitadas exitosamente');
    }
  } catch (error) {
    console.error('Error habilitando notificaciones:', error);
  }
}

/**
 * Muestra ayuda detallada para habilitar permisos
 */
function showPermissionHelp(): void {
  const helpModal = document.createElement('div');
  helpModal.className = 'fixed inset-0 z-60 bg-black bg-opacity-50 flex items-center justify-center p-4';
  helpModal.innerHTML = `
    <div class="bg-white rounded-lg p-6 max-w-md w-full">
      <div class="flex items-center mb-4">
        <div class="bg-blue-100 p-3 rounded-full mr-3">
          <i class="fas fa-bell text-blue-600"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900">Habilitar Notificaciones</h3>
      </div>
      
      <div class="space-y-3 mb-6">
        <p class="text-sm text-gray-600">Para recibir alertas importantes:</p>
        <ol class="text-sm text-gray-700 space-y-2 list-decimal list-inside">
          <li>Haz clic en el ícono de <strong>candado 🔒</strong> o <strong>información ℹ️</strong> junto a la URL</li>
          <li>Busca <strong>"Notificaciones"</strong> en la configuración</li>
          <li>Cambia de <strong>"Bloquear"</strong> a <strong>"Permitir"</strong></li>
          <li>Recarga la página</li>
        </ol>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button onclick="this.closest('.fixed').remove()" class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
          Entendido
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(helpModal);
  
  // Cerrar al hacer clic en el overlay
  helpModal.addEventListener('click', (e) => {
    if (e.target === helpModal) {
      helpModal.remove();
    }
  });
}

